import { supabase } from '../lib/supabase.js';
import { listRouteServiceDays } from './repository.js';
import { browserlessMonthlyRules } from './browserless-monthly-ui.js';

const WEEKLY_STALE_AFTER_DAYS = 8;
const MONTHLY_STALE_AFTER_DAYS = 35;
const DAY_MS = 24 * 60 * 60 * 1000;
const CALCULATOR_NAMES = {
  'etoll-pl':'e-TOLL Polska',
  'myto-cz':'MYTO Czechy',
  'emyto-sk':'eMyto Słowacja',
  'darsgo-si':'DarsGo Słowenia',
  'asfinag-at':'ASFINAG Austria',
  'hugo-hu':'HU-GO Węgry',
  'a1-amberone-pl':'A1 AmberOne',
  'a2-awsa-pl':'A2 Autostrada Wielkopolska',
  'a4-sam-pl':'A4 Katowice–Kraków',
  'hac-hr':'HAC Chorwacja',
  'azm-hr':'Zagreb–Macelj',
  'bina-istra-hr':'Bina-Istra',
  'autostrade-it':'Autostrade Italia',
  'autoroutes-fr':'Autoroutes Francja',
};

const MONTHLY_AUTOMATION_TABLE = 'browserless_monthly_automation';
const MONTHLY_AUTOMATION_REQUIRED = browserlessMonthlyRules.required;
const MONTHLY_AUTOMATION_LIMITS = browserlessMonthlyRules.limits;
const MONTHLY_AUTOMATION_MOTORWAY = browserlessMonthlyRules.motorway;
const SETTING_LABELS = {
  vehicleCategory:'kategoria pojazdu',
  baseAxles:'podstawowa liczba osi',
  actualAxles:'rzeczywista liczba osi',
  emissionClass:'klasa EURO / emisji',
  co2Class:'klasa CO₂',
  entryCountry:'kraj wjazdu',
  exitCountry:'kraj wyjazdu',
  axles:'liczba osi',
  vehicleType:'typ autobusu',
  category:'kategoria osi',
  axleWeight:'nacisk osi',
  grossWeight:'DMC',
  height:'wysokość',
  width:'szerokość',
  length:'długość',
  entryBorderValue:'przejście graniczne WJAZD',
  exitBorderValue:'przejście graniczne WYJAZD',
};

const SERVICE_PATTERN_DEFS = {
  all:{ label:'Cały miesiąc', outbound:new Set([0,1,2,3,4,5,6]), return:new Set([0,1,2,3,4,5,6]) },
  five:{ label:'5 dni w tygodniu', outbound:new Set([0,3,4,5,6]), return:new Set([0,1,4,5,6]) },
  four:{ label:'4 dni w tygodniu', outbound:new Set([0,4,5,6]), return:new Set([0,1,5,6]) },
  three:{ label:'3 dni w tygodniu', outbound:new Set([0,5,6]), return:new Set([0,1,6]) },
  none:{ label:'Nie kursowało', outbound:new Set(), return:new Set() },
};

let initialized = false;
let refreshToken = 0;
let cached = null;
let objectUrls = [];

function $(id) { return document.getElementById(id); }

export function initDiagnostics() {
  if (initialized) return;
  initialized = true;

  $('diagnosticsBtn')?.addEventListener('click', openDiagnostics);
  $('closeDiagnosticsBtn')?.addEventListener('click', closeDiagnostics);
  $('refreshDiagnosticsBtn')?.addEventListener('click', () => refreshDiagnostics(true));
  $('diagnosticsSearch')?.addEventListener('input', renderDiagnostics);
  $('diagnosticsStateFilter')?.addEventListener('change', renderDiagnostics);
  $('diagnosticsLines')?.addEventListener('click', handleDiagnosticsClick);
  window.addEventListener('d24:calculation-saved', () => {
    if (!$('diagnosticsWorkspace')?.classList.contains('hidden')) refreshDiagnostics(false);
  });
}

function openDiagnostics() {
  $('mainWorkspace')?.classList.add('hidden');
  $('diagnosticsWorkspace')?.classList.remove('hidden');
  $('diagnosticsBtn')?.classList.add('active');
  refreshDiagnostics(true);
}

function closeDiagnostics() {
  $('diagnosticsWorkspace')?.classList.add('hidden');
  $('mainWorkspace')?.classList.remove('hidden');
  $('diagnosticsBtn')?.classList.remove('active');
}

export async function refreshDiagnostics(showLoading = true) {
  if (!supabase) return;
  const token = ++refreshToken;
  if (showLoading) setStatus('Wczytuję stan wszystkich linii i kalkulatorów…', 'working');
  clearObjectUrls();

  try {
    const data = await loadDiagnosticsData();
    if (token !== refreshToken) return;
    cached = buildViewModel(data);
    renderDiagnostics();
    const readinessText = cached.readiness?.available
      ? ` Automaty miesięczne: ${cached.readiness.ready}/${cached.readiness.total} gotowe.`
      : ' Gotowość automatów jest chwilowo niedostępna.';
    setStatus(
      `Stan na ${formatDateTime(new Date().toISOString())}. Dane bieżące i historia zostały odświeżone.${readinessText}`,
      'ok'
    );
  } catch (error) {
    console.error('[D24 DIAGNOSTYKA]', error);
    setStatus(`Nie udało się wczytać diagnostyki: ${error.message}`, 'error');
    const list = $('diagnosticsLines');
    if (list) list.innerHTML = '<div class="empty">Brak danych diagnostycznych.</div>';
  }
}

async function loadDiagnosticsData() {
  const { data:{ session }, error:sessionError } = await supabase.auth.getSession();
  if (sessionError) throw sessionError;
  if (!session?.access_token) throw new Error('Sesja wygasła. Zaloguj się ponownie.');

  const routes = await loadPagedRows(() => supabase
    .from('routes')
    .select('id,organization_id,name,route_number,direction,direction_type,carrier,current_version_id,updated_at,countries')
    .not('current_version_id', 'is', null)
    .order('route_number', { ascending:true })
    .order('id', { ascending:true }));

  const versionIds = [...new Set((routes || []).map(row => row.current_version_id).filter(Boolean))];
  let sections = [];
  let currentResults = [];
  if (versionIds.length) {
    // Przy większej liczbie linii country_sections i calculation_results
    // przekraczają domyślny limit 1000 rekordów PostgREST. Czytamy je
    // stronicami i dodatkowo porcjujemy listę route_version_id, żeby nie
    // budować zbyt długiego parametru .in(...) w jednym żądaniu.
    [sections, currentResults] = await Promise.all([
      loadPagedRowsForIds(versionIds, ids => supabase
        .from('country_sections')
        .select('id,route_version_id,section_order,country_code,country_name,calculator_key,status,points')
        .in('route_version_id', ids)
        .order('route_version_id', { ascending:true })
        .order('section_order', { ascending:true })),
      loadPagedRowsForIds(versionIds, ids => supabase
        .from('calculation_results')
        .select('route_version_id,country_section_order,country_code,calculator_key,cost_value,cost_text,currency,status,calculated_at,raw_result')
        .in('route_version_id', ids)
        .order('calculated_at', { ascending:false })
        .order('route_version_id', { ascending:true })),
    ]);
  }

  let history = [];
  let historyAvailable = true;
  const historyResponse = await supabase
    .from('calculation_history')
    .select('id,route_number,route_id,route_version_id,section_order,country_code,country_name,calculator_key,cost_value,cost_text,currency,distance_km,settings_summary,source,status,error_message,job_id,screenshot_bucket,screenshot_path,calculated_at,created_at')
    .order('created_at', { ascending:false })
    .limit(4000);

  if (historyResponse.error) {
    historyAvailable = false;
    console.warn('[D24 DIAGNOSTYKA] Tabela calculation_history niedostępna:', historyResponse.error);
  } else {
    history = historyResponse.data || [];
  }

  let serviceDays = [];
  let monthlyAutomation = [];
  let calculatorSettings = [];
  let readinessAvailable = true;
  let readinessError = '';
  const serviceMonth = currentMonth();
  const organizationId = (routes || []).find(route => route.organization_id)?.organization_id || null;
  if (organizationId) {
    try {
      // Używamy tego samego stronicowanego odczytu co Raporty. Bez tego
      // diagnostyka również zatrzymywała się na limicie 1000 rekordów RPC.
      serviceDays = await listRouteServiceDays(organizationId, serviceMonth);
    } catch (serviceError) {
      console.warn('[D24 DIAGNOSTYKA] Nie udało się pobrać kursowania linii:', serviceError);
    }

    try {
      [monthlyAutomation, calculatorSettings] = await Promise.all([
        loadPagedRows(() => supabase
          .from(MONTHLY_AUTOMATION_TABLE)
          .select('organization_id,route_id,section_order,calculator_key,enabled,schedule_mode,schedule_day,next_run_on')
          .eq('organization_id', organizationId)
          .eq('enabled', true)),
        loadPagedRows(() => supabase
          .from('line_calculator_settings')
          .select('organization_id,route_number,calculator_key,settings,updated_at')
          .eq('organization_id', organizationId)),
      ]);
    } catch (readinessLoadError) {
      readinessAvailable = false;
      readinessError = String(readinessLoadError?.message || readinessLoadError || 'Nie udało się pobrać konfiguracji automatów.');
      console.warn('[D24 DIAGNOSTYKA] Gotowość automatów niedostępna:', readinessLoadError);
    }
  }

  return {
    routes:routes || [],
    sections,
    currentResults,
    history,
    historyAvailable,
    serviceDays,
    serviceMonth,
    monthlyAutomation,
    calculatorSettings,
    readinessAvailable,
    readinessError,
  };
}


async function loadPagedRows(buildQuery, pageSize = 1000) {
  const rows = [];
  for (let from = 0; ; from += pageSize) {
    const { data, error } = await buildQuery().range(from, from + pageSize - 1);
    if (error) throw error;
    const page = data || [];
    rows.push(...page);
    if (page.length < pageSize) break;
    if (rows.length >= 50000) {
      throw new Error('Diagnostyka przekroczyła bezpieczny limit 50 000 rekordów jednego zbioru.');
    }
  }
  return rows;
}

async function loadPagedRowsForIds(ids, buildQuery, chunkSize = 100) {
  const uniqueIds = [...new Set((ids || []).filter(Boolean))];
  const rows = [];
  for (let index = 0; index < uniqueIds.length; index += chunkSize) {
    const chunk = uniqueIds.slice(index, index + chunkSize);
    const pageRows = await loadPagedRows(() => buildQuery(chunk));
    rows.push(...pageRows);
    if (rows.length >= 50000) {
      throw new Error('Diagnostyka przekroczyła bezpieczny limit 50 000 rekordów.');
    }
  }
  return rows;
}

function buildViewModel(data) {
  const sectionsByVersion = groupBy(data.sections, row => row.route_version_id);
  const resultsByVersion = groupBy(data.currentResults, row => row.route_version_id);
  const historyByLineCalculator = groupBy(
    data.history,
    row => historyKey(row.route_number, row.country_code, row.calculator_key)
  );
  const lineMap = new Map();

  for (const route of data.routes) {
    const lineNumber = String(route.route_number || route.name || '').trim();
    if (!lineNumber) continue;
    if (!lineMap.has(lineNumber)) {
      lineMap.set(lineNumber, {
        lineNumber,
        title:route.name || lineNumber,
        carrier:String(route.carrier || '').trim().toUpperCase(),
        routes:[],
        calculators:new Map(),
      });
    }
    const line = lineMap.get(lineNumber);
    line.routes.push(route);

    const versionSections = sectionsByVersion.get(route.current_version_id) || [];
    const versionResults = resultsByVersion.get(route.current_version_id) || [];
    for (const section of versionSections) {
      const calculatorKey = String(section.calculator_key || '').trim();
      const calcKey = `${section.country_code || ''}::${calculatorKey || `section-${section.section_order}`}`;
      const current = versionResults.find(result =>
        Number(result.country_section_order) === Number(section.section_order)
        && String(result.calculator_key || '') === calculatorKey
      ) || versionResults.find(result => Number(result.country_section_order) === Number(section.section_order)) || null;
      const existing = line.calculators.get(calcKey);
      const currentDate = current?.calculated_at || '';
      if (!existing || compareDates(currentDate, existing.current?.calculated_at) > 0) {
        line.calculators.set(calcKey, {
          section,
          current,
          calculatorKey,
          countryCode:section.country_code || current?.country_code || '',
          countryName:section.country_name || countryLabel(section.country_code),
        });
      }
    }
  }

  const lines = [...lineMap.values()].map(line => {
    const calculators = [...line.calculators.values()].map(item => {
      const key = historyKey(line.lineNumber, item.countryCode, item.calculatorKey);
      let history = (historyByLineCalculator.get(key) || []).slice();
      if (item.current) {
        const currentRow = {
          id:`current:${line.lineNumber}:${item.countryCode}:${item.calculatorKey}`,
          route_number:line.lineNumber,
          country_code:item.countryCode,
          country_name:item.countryName,
          calculator_key:item.calculatorKey,
          cost_value:item.current.cost_value,
          cost_text:item.current.cost_text,
          currency:item.current.currency,
          source:item.current?.raw_result?.source || (item.calculatorKey === 'etoll-pl' ? 'oracle' : 'browserless'),
          status:item.current.status || 'completed',
          calculated_at:item.current.calculated_at,
          created_at:item.current.calculated_at,
          screenshot_bucket:'',
          screenshot_path:'',
          error_message:'',
          settings_summary:settingsFromRaw(item.current.raw_result),
          is_current_result:true,
        };
        const duplicate = history.some(row => (
          row.status === currentRow.status
          && String(row.calculator_key || '') === String(currentRow.calculator_key || '')
          && Math.abs(eventTimestamp(row) - eventTimestamp(currentRow)) < 1500
          && String(row.cost_text || '') === String(currentRow.cost_text || '')
        ));
        if (!duplicate) history.push(currentRow);
      }
      history.sort((a,b) => eventTimestamp(b) - eventTimestamp(a));
      const successes = history.filter(row => row.status === 'completed');
      const lastSuccess = successes[0] || null;
      const previousSuccess = successes[1] || null;
      const lastAttempt = history[0] || lastSuccess;
      const diff = priceDifference(lastSuccess, previousSuccess);
      const status = freshnessStatus(lastSuccess, lastAttempt, item.calculatorKey);
      return {
        ...item,
        history,
        lastSuccess,
        previousSuccess,
        lastAttempt,
        diff,
        status,
        source:sourceLabel(lastSuccess?.source || item.current?.raw_result?.source || '', item.calculatorKey),
        calculatorName:CALCULATOR_NAMES[item.calculatorKey] || item.calculatorKey || 'Brak kalkulatora',
      };
    }).sort((a,b) => String(a.countryName).localeCompare(String(b.countryName), 'pl'));

    return {
      ...line,
      calculators,
      servicePattern:detectLineServicePattern(line, data.serviceDays || [], data.serviceMonth || currentMonth()),
      worstStatus:worstStatus(calculators.map(item => item.status.kind)),
      latestUpdate:maxDate(calculators.map(item => eventIso(item.lastSuccess))),
    };
  }).sort((a,b) => a.lineNumber.localeCompare(b.lineNumber, 'pl', { numeric:true }));

  const readiness = buildMonthlyAutomationReadiness(data);
  const all = lines.flatMap(line => line.calculators.map(calculator => ({ line, calculator })));
  return {
    ...data,
    lines,
    readiness,
    summary:{
      lineCount:lines.length,
      staleCount:all.filter(item => ['stale','missing'].includes(item.calculator.status.kind) && item.calculator.calculatorKey).length,
      errorCount:all.filter(item => item.calculator.status.kind === 'error').length,
      changedCount:all.filter(item => Number.isFinite(item.calculator.diff?.value) && Math.abs(item.calculator.diff.value) > 0.000001).length,
      automationTotal:readiness.total,
      automationReady:readiness.ready,
      automationProblem:readiness.problem,
    },
  };
}


function buildMonthlyAutomationReadiness(data) {
  if (!data.readinessAvailable) {
    return {
      available:false,
      error:data.readinessError || 'Dane gotowości automatów są niedostępne.',
      total:0,
      ready:0,
      problem:0,
      items:[],
    };
  }

  const routesById = new Map((data.routes || []).map(route => [String(route.id || ''), route]));
  const sectionsByVersion = groupBy(data.sections || [], row => String(row.route_version_id || ''));
  const settingsByLineCalculator = new Map(
    (data.calculatorSettings || []).map(row => [
      `${String(row.route_number || '').trim()}::${String(row.calculator_key || '').trim()}`,
      row.settings && typeof row.settings === 'object' ? row.settings : {},
    ])
  );

  const items = (data.monthlyAutomation || [])
    .filter(row => row?.enabled)
    .map(row => {
      const route = routesById.get(String(row.route_id || '')) || null;
      const lineNumber = String(route?.route_number || route?.name || '').trim();
      const calculatorKey = String(row.calculator_key || '').trim();
      const configuredSectionOrder = Number(row.section_order || 0);
      const binding = resolveMonthlyAutomationSection(route, row, sectionsByVersion);
      const section = binding.section;
      const sectionOrder = section
        ? Number(section.section_order || 0)
        : configuredSectionOrder;
      const baseSettings = settingsByLineCalculator.get(`${lineNumber}::${calculatorKey}`) || null;
      const settings = mergedSectionSettings(baseSettings, sectionOrder, configuredSectionOrder);
      const issues = [];

      if (!route) {
        issues.push('Nie znaleziono zapisanej linii przypisanej do automatu.');
      } else if (!section && binding.reason === 'ambiguous') {
        issues.push(`Nie można jednoznacznie przypisać automatu do aktualnej sekcji ${CALCULATOR_NAMES[calculatorKey] || calculatorKey}: bieżąca trasa ma ${binding.candidateCount} takie sekcje.`);
      } else if (!section) {
        issues.push('Nie znaleziono aktualnej sekcji trasy przypisanej do automatu.');
      }

      if (section) {
        const points = Array.isArray(section.points) ? section.points : [];
        const limit = MONTHLY_AUTOMATION_LIMITS[calculatorKey];
        if (limit && points.length > limit) {
          issues.push(`Odcinek ma ${points.length} punktów, a automat obsługuje maksymalnie ${limit}.`);
        }

        const required = [...(MONTHLY_AUTOMATION_REQUIRED[calculatorKey] || [])];
        if (calculatorKey === 'myto-cz') {
          if (isMonthlyBorderPoint(points[0])) required.push('entryBorderValue');
          if (isMonthlyBorderPoint(points.at(-1))) required.push('exitBorderValue');
        }

        if (required.length && !settings) {
          issues.push('Brak zapisanych parametrów kalkulatora dla tej linii.');
        } else {
          const missing = [...new Set(required)]
            .filter(name => String(settings?.[name] ?? '').trim() === '')
            .map(name => SETTING_LABELS[name] || name);
          if (missing.length) issues.push(`Brak: ${missing.join(', ')}.`);
        }

        if (MONTHLY_AUTOMATION_MOTORWAY.has(calculatorKey)) {
          const entryName = String(
            settings?.entryName
            || points[0]?.name
            || ''
          ).trim();
          const exitName = String(
            settings?.exitName
            || points.at(-1)?.name
            || ''
          ).trim();
          if (!entryName || !exitName) {
            issues.push('Brak zapisanego punktu wjazdu lub wyjazdu.');
          }
          if (calculatorKey === 'hac-hr' && !String(settings?.roadGroup || '').trim()) {
            issues.push('Brak zapisanej grupy autostrady HAC.');
          }
        }
      }

      return {
        routeId:String(row.route_id || ''),
        lineNumber:lineNumber || 'Nieznana linia',
        carrier:String(route?.carrier || '').trim().toUpperCase(),
        directionType:normalizeDirectionType(route?.direction_type),
        calculatorKey,
        calculatorName:CALCULATOR_NAMES[calculatorKey] || browserlessMonthlyRules.labels?.[calculatorKey] || calculatorKey || 'Nieznany kalkulator',
        sectionOrder,
        configuredSectionOrder,
        sectionBinding:binding.reason,
        scheduleLabel:monthlyScheduleLabel(row),
        ready:issues.length === 0,
        issues,
      };
    })
    .sort((a, b) => (
      compareLineNumbers(a.lineNumber, b.lineNumber)
      || a.calculatorName.localeCompare(b.calculatorName, 'pl', { numeric:true, sensitivity:'base' })
      || a.sectionOrder - b.sectionOrder
    ));

  return {
    available:true,
    error:'',
    total:items.length,
    ready:items.filter(item => item.ready).length,
    problem:items.filter(item => !item.ready).length,
    items,
  };
}

function resolveMonthlyAutomationSection(route, automationRow, sectionsByVersion) {
  if (!route?.current_version_id) {
    return { section:null, reason:'missing-route', candidateCount:0 };
  }

  const calculatorKey = String(automationRow?.calculator_key || '').trim();
  const configuredOrder = Number(automationRow?.section_order || 0);
  const sections = sectionsByVersion.get(String(route.current_version_id || '')) || [];

  const exact = sections.find(item => (
    Number(item.section_order) === configuredOrder
    && String(item.calculator_key || '') === calculatorKey
  ));
  if (exact) return { section:exact, reason:'exact', candidateCount:1 };

  // section_order jest numerem bieżącego snapshotu i może zmienić się po
  // ponownym wyznaczeniu/zapisie trasy. Jeżeli w aktualnej wersji istnieje
  // dokładnie jedna sekcja danego kalkulatora, jest to bezpieczne i
  // jednoznaczne przypisanie. Przy wielu sekcjach nie zgadujemy.
  const candidates = sections.filter(item => (
    String(item.calculator_key || '') === calculatorKey
  ));
  if (candidates.length === 1) {
    return { section:candidates[0], reason:'calculator-key', candidateCount:1 };
  }

  return {
    section:null,
    reason:candidates.length > 1 ? 'ambiguous' : 'missing',
    candidateCount:candidates.length,
  };
}

function mergedSectionSettings(settings, sectionOrder, fallbackSectionOrder = null) {
  if (!settings || typeof settings !== 'object') return null;
  const routes = settings.sectionRoutes && typeof settings.sectionRoutes === 'object'
    ? settings.sectionRoutes
    : {};
  const currentKey = String(Number(sectionOrder || 0));
  const fallbackKey = fallbackSectionOrder === null
    ? ''
    : String(Number(fallbackSectionOrder || 0));
  const fallbackSettings = fallbackKey && fallbackKey !== currentKey
    ? (routes[fallbackKey] || {})
    : {};
  const currentSettings = routes[currentKey] || {};
  return { ...settings, ...fallbackSettings, ...currentSettings };
}

function isMonthlyBorderPoint(point) {
  return Boolean(
    point?.isBorder
    || point?.is_border
    || point?.pointType === 'border'
    || point?.point_type === 'border'
    || String(point?.countryCode || point?.country_code || '').includes('/')
    || /^granica\b/i.test(String(point?.name || '').trim())
  );
}

function monthlyScheduleLabel(row) {
  if (String(row?.schedule_mode || '') === 'day_of_month') {
    const day = Number(row?.schedule_day);
    return Number.isFinite(day) && day >= 1 && day <= 31
      ? `${day}. dzień miesiąca`
      : 'konkretny dzień — brak poprawnej daty';
  }
  return 'ostatni dzień miesiąca';
}

function compareLineNumbers(aValue, bValue) {
  const a = String(aValue || '').trim();
  const b = String(bValue || '').trim();
  const aNumeric = /^\d/.test(a);
  const bNumeric = /^\d/.test(b);
  if (aNumeric !== bNumeric) return aNumeric ? -1 : 1;
  return a.localeCompare(b, 'pl', { numeric:true, sensitivity:'base' });
}

function renderAutomationReadiness() {
  const readiness = cached?.readiness;
  const panel = $('diagnosticsReadinessPanel');
  const list = $('diagAutomationIssues');
  if (!panel || !list || !readiness) return;

  if (!readiness.available) {
    panel.classList.add('is-unavailable');
    list.innerHTML = `<div class="diagnostics-readiness-unavailable">Nie udało się sprawdzić gotowości automatów: ${esc(readiness.error)}</div>`;
    return;
  }

  panel.classList.remove('is-unavailable');
  const problems = readiness.items.filter(item => !item.ready);
  if (!problems.length) {
    list.innerHTML = readiness.total
      ? '<div class="diagnostics-readiness-ok">Wszystkie włączone automaty miesięczne mają komplet wymaganych ustawień.</div>'
      : '<div class="diagnostics-readiness-empty">Nie ma obecnie włączonych automatów miesięcznych Browserless.</div>';
    return;
  }

  list.innerHTML = problems.map(item => `
    <div class="diagnostics-readiness-row">
      <div class="diagnostics-readiness-route">
        <strong>${esc(item.lineNumber)}</strong>
        ${item.carrier ? `<span>${esc(item.carrier)}</span>` : ''}
      </div>
      <div class="diagnostics-readiness-calculator">
        <strong>${esc(item.calculatorName)}</strong>
        <span>${esc(item.scheduleLabel)}</span>
      </div>
      <div class="diagnostics-readiness-problem">${esc(item.issues.join(' '))}</div>
    </div>
  `).join('');
}

function renderDiagnostics() {
  if (!cached) return;
  renderSummary();
  renderAutomationReadiness();
  const container = $('diagnosticsLines');
  if (!container) return;
  const search = normalize($('diagnosticsSearch')?.value || '');
  const filter = $('diagnosticsStateFilter')?.value || '';
  const lines = cached.lines.filter(line => {
    const haystack = normalize(`${line.lineNumber} ${line.title} ${line.servicePattern || ''} ${line.calculators.map(c => `${c.countryName} ${c.calculatorName}`).join(' ')}`);
    if (search && !haystack.includes(search)) return false;
    if (filter && !line.calculators.some(calc => calc.status.kind === filter)) return false;
    return true;
  });

  container.textContent = '';
  if (!lines.length) {
    container.innerHTML = '<div class="empty">Brak linii pasujących do filtrów.</div>';
    return;
  }

  for (const line of lines) {
    const details = document.createElement('details');
    details.className = `diagnostics-line-card state-${line.worstStatus}`;
    const statusCounts = countKinds(line.calculators.map(c => c.status.kind));
    details.innerHTML = `
      <summary class="diagnostics-line-summary">
        <div>
          <div class="diagnostics-line-title-row">
            <strong>${esc(line.lineNumber)}</strong>
            <span class="diagnostics-line-relation">${esc(line.title && line.title !== line.lineNumber ? line.title : '')}</span>
            <span class="diagnostics-service-pattern">Kursowanie: ${esc(line.servicePattern || 'Własne')}</span>
          </div>
          <div class="diagnostics-line-countries">${line.calculators.map(countryChip).join('')}</div>
        </div>
        <div class="diagnostics-line-meta">
          <span>${statusCounts.error ? `${statusCounts.error} bł.` : ''}</span>
          <span>${statusCounts.stale || statusCounts.missing ? `${(statusCounts.stale || 0) + (statusCounts.missing || 0)} do aktualizacji` : ''}</span>
          <span>Ostatnio: <b>${line.latestUpdate ? formatDate(line.latestUpdate) : 'brak'}</b></span>
        </div>
      </summary>
      <div class="diagnostics-country-list">
        ${line.calculators.map(calc => renderCalculatorRow(line, calc)).join('')}
      </div>
    `;
    container.appendChild(details);
  }
}

function detectLineServicePattern(line, serviceDays, month) {
  const directions = new Set(
    (line.routes || [])
      .map(route => normalizeDirectionType(route.direction_type))
      .filter(Boolean)
  );
  if (!directions.size) return 'Własne';

  const carrier = String(line.carrier || '').trim().toUpperCase();
  const limit = reportingDayLimit(month);
  const selected = new Set(
    (serviceDays || [])
      .filter(row => String(row.route_number || '').trim() === String(line.lineNumber || '').trim())
      .filter(row => !carrier || String(row.carrier || '').trim().toUpperCase() === carrier)
      .filter(row => String(row.service_date || '').slice(0,7) === month)
      .filter(row => Number(String(row.service_date || '').slice(8,10)) <= limit)
      .map(row => `${String(row.service_date || '').slice(0,10)}::${normalizeDirectionType(row.direction_type)}`)
  );

  for (const pattern of Object.values(SERVICE_PATTERN_DEFS)) {
    const expected = new Set();
    for (let day = 1; day <= limit; day += 1) {
      const date = `${month}-${String(day).padStart(2,'0')}`;
      const weekday = weekdayForDate(date);
      for (const direction of directions) {
        if (pattern[direction]?.has(weekday)) expected.add(`${date}::${direction}`);
      }
    }
    if (setsEqual(selected, expected)) return pattern.label;
  }
  return 'Własne';
}

function setsEqual(a, b) {
  if (a.size !== b.size) return false;
  for (const value of a) if (!b.has(value)) return false;
  return true;
}

function weekdayForDate(dateText) {
  const [year, month, day] = String(dateText || '').split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day)).getUTCDay();
}

function reportingDayLimit(month) {
  const [year, monthNumber] = String(month || '').split('-').map(Number);
  if (!year || !monthNumber) return 31;
  const dayCount = new Date(Date.UTC(year, monthNumber, 0)).getUTCDate();
  const now = new Date();
  if (year === now.getFullYear() && monthNumber === now.getMonth() + 1) return Math.min(now.getDate(), dayCount);
  return dayCount;
}

function currentMonth() {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2,'0')}`;
}

function renderSummary() {
  const { summary } = cached;
  setText('diagLineCount', summary.lineCount);
  setText('diagStaleCount', summary.staleCount);
  setText('diagErrorCount', summary.errorCount);
  setText('diagChangedCount', summary.changedCount);
  setText('diagAutomationTotal', summary.automationTotal);
  setText('diagAutomationReady', summary.automationReady);
  setText('diagAutomationProblem', summary.automationProblem);
}

function renderCalculatorRow(line, calc) {
  const lastSuccessDate = eventIso(calc.lastSuccess);
  const lastAttemptDate = eventIso(calc.lastAttempt);
  const currentPrice = calc.lastSuccess?.cost_text || formatCost(calc.lastSuccess);
  const diff = formatDifference(calc.diff, calc.lastSuccess?.currency);
  const hasHistory = calc.history.length > 0;
  const historyRows = calc.history.slice(0, 20).map(row => `
    <tr>
      <td>${esc(formatDateTime(eventIso(row)))}</td>
      <td>${esc(row.status === 'completed' ? (row.cost_text || formatCost(row)) : '—')}</td>
      <td>${esc(sourceLabel(row.source, calc.calculatorKey))}</td>
      <td>${row.status === 'completed' ? '<span class="diag-mini-ok">OK</span>' : `<span class="diag-mini-error">${esc(row.error_message || row.status || 'Błąd')}</span>`}</td>
    </tr>
  `).join('');
  const screenshotCount = calc.history.filter(row => row.screenshot_path).slice(0,3).length;
  return `
    <article class="diagnostics-country-card state-${calc.status.kind}" data-line="${escAttr(line.lineNumber)}" data-country="${escAttr(calc.countryCode)}" data-calculator="${escAttr(calc.calculatorKey)}">
      <div class="diagnostics-country-head">
        <div>
          <div class="diagnostics-country-title"><span class="country-code-pill">${esc(calc.countryCode || '—')}</span><strong>${esc(calc.countryName || 'Nieznane')}</strong><span>${esc(calc.calculatorName)}</span></div>
          <div class="diagnostics-country-sub">Źródło: <b>${esc(calc.source)}</b> · ${esc(calc.status.label)}</div>
        </div>
        <span class="diagnostics-state-badge ${calc.status.kind}">${esc(calc.status.short)}</span>
      </div>
      <div class="diagnostics-country-grid">
        <div><span>Aktualna cena</span><strong>${esc(currentPrice || 'Brak wyniku')}</strong></div>
        <div><span>Ostatnia poprawna aktualizacja</span><strong>${esc(lastSuccessDate ? formatDateTime(lastSuccessDate) : 'Brak')}</strong></div>
        <div><span>Ostatnia próba</span><strong>${esc(lastAttemptDate ? formatDateTime(lastAttemptDate) : 'Brak')}</strong></div>
        <div><span>Zmiana ceny</span><strong class="${calc.diff?.value > 0 ? 'price-up' : calc.diff?.value < 0 ? 'price-down' : ''}">${esc(diff)}</strong></div>
      </div>
      ${calc.lastAttempt?.status && calc.lastAttempt.status !== 'completed' ? `<div class="diagnostics-error-box">Ostatni błąd: ${esc(calc.lastAttempt.error_message || calc.lastAttempt.status)}</div>` : ''}
      <div class="diagnostics-country-actions">
        <button class="button small secondary diag-toggle-history" type="button">${hasHistory ? `Historia (${calc.history.length})` : 'Historia'}</button>
        <button class="button small secondary diag-load-screens" type="button" ${screenshotCount ? '' : 'disabled'}>3 ostatnie zrzuty${screenshotCount ? ` (${screenshotCount})` : ''}</button>
      </div>
      <div class="diagnostics-history-panel hidden">
        <div class="diagnostics-history-table-wrap">
          <table class="diagnostics-history-table"><thead><tr><th>Data</th><th>Cena</th><th>Źródło</th><th>Stan</th></tr></thead><tbody>${historyRows || '<tr><td colspan="4">Brak historii.</td></tr>'}</tbody></table>
        </div>
        <div class="diagnostics-screenshots" data-role="screenshots"></div>
      </div>
    </article>
  `;
}

async function handleDiagnosticsClick(event) {
  const card = event.target.closest('.diagnostics-country-card');
  if (!card) return;
  if (event.target.closest('.diag-toggle-history')) {
    const panel = card.querySelector('.diagnostics-history-panel');
    panel?.classList.toggle('hidden');
    return;
  }
  const screenButton = event.target.closest('.diag-load-screens');
  if (screenButton) {
    const panel = card.querySelector('.diagnostics-history-panel');
    panel?.classList.remove('hidden');
    await loadScreenshotsForCard(card, screenButton);
  }
}

async function loadScreenshotsForCard(card, button) {
  const lineNumber = card.dataset.line || '';
  const country = card.dataset.country || '';
  const calculator = card.dataset.calculator || '';
  const line = cached?.lines.find(item => item.lineNumber === lineNumber);
  const calc = line?.calculators.find(item => item.countryCode === country && item.calculatorKey === calculator);
  const target = card.querySelector('[data-role="screenshots"]');
  if (!target || !calc) return;

  const rows = calc.history.filter(row => row.screenshot_path).slice(0,3);
  if (!rows.length) {
    target.innerHTML = '<div class="empty">Brak zapisanych zrzutów.</div>';
    return;
  }

  button.disabled = true;
  button.textContent = 'Wczytuję zrzuty…';
  target.innerHTML = '<div class="status working">Pobieram 3 ostatnie zrzuty ze Storage…</div>';
  try {
    const items = await Promise.all(rows.map(async row => {
      const bucket = row.screenshot_bucket || 'etoll-screenshots';
      const { data, error } = await supabase.storage.from(bucket).download(row.screenshot_path);
      if (error) throw error;
      const url = URL.createObjectURL(data);
      objectUrls.push(url);
      return { row, url };
    }));
    target.innerHTML = items.map(({ row, url }, index) => `
      <figure class="diagnostics-screenshot-card">
        <a href="${escAttr(url)}" target="_blank" rel="noopener"><img src="${escAttr(url)}" alt="Zrzut ${index + 1}"></a>
        <figcaption>${esc(formatDateTime(eventIso(row)))}<br><strong>${esc(row.cost_text || formatCost(row))}</strong></figcaption>
      </figure>
    `).join('');
    button.textContent = `Zrzuty (${items.length})`;
  } catch (error) {
    console.error('[D24 DIAGNOSTYKA] screenshot:', error);
    target.innerHTML = `<div class="status error">Nie udało się pobrać zrzutów: ${esc(error.message)}</div>`;
    button.textContent = 'Spróbuj ponownie';
    button.disabled = false;
  }
}

function freshnessStatus(lastSuccess, lastAttempt, calculatorKey) {
  if (!calculatorKey) return { kind:'neutral', short:'N/D', label:'Brak kalkulatora dla tego odcinka' };
  if (lastAttempt && lastAttempt.status && lastAttempt.status !== 'completed') {
    const attemptDate = eventTimestamp(lastAttempt);
    const successDate = eventTimestamp(lastSuccess);
    if (!lastSuccess || attemptDate >= successDate) return { kind:'error', short:'Błąd', label:'Ostatnia próba zakończyła się błędem' };
  }
  if (!lastSuccess) return { kind:'missing', short:'Brak', label:'Nigdy nie zapisano poprawnego wyniku' };
  const age = Date.now() - eventTimestamp(lastSuccess);
  const staleAfterDays = calculatorKey === 'etoll-pl' ? WEEKLY_STALE_AFTER_DAYS : MONTHLY_STALE_AFTER_DAYS;
  if (!Number.isFinite(age) || age > staleAfterDays * DAY_MS) return { kind:'stale', short:'Do aktualizacji', label:`Ponad ${staleAfterDays} dni od ostatniego poprawnego wyniku` };
  return { kind:'ok', short:'Aktualne', label:'Wynik aktualny' };
}

function eventTimestamp(row) {
  if (!row) return 0;
  const created = new Date(row.created_at || 0).getTime();
  const calculated = new Date(row.calculated_at || 0).getTime();
  const values = [created, calculated].filter(Number.isFinite);
  return values.length ? Math.max(...values) : 0;
}

function eventIso(row) {
  const timestamp = eventTimestamp(row);
  return timestamp ? new Date(timestamp).toISOString() : '';
}

function priceDifference(current, previous) {
  const a = finite(current?.cost_value);
  const b = finite(previous?.cost_value);
  if (a === null || b === null) return null;
  const value = a - b;
  const percent = b !== 0 ? (value / b) * 100 : null;
  return { value, percent };
}

function formatDifference(diff, currency) {
  if (!diff) return 'Brak porównania';
  const sign = diff.value > 0 ? '+' : '';
  const pct = Number.isFinite(diff.percent) ? ` (${diff.percent > 0 ? '+' : ''}${diff.percent.toFixed(2)}%)` : '';
  return `${sign}${diff.value.toFixed(2)}${currency ? ` ${currency}` : ''}${pct}`;
}

function formatCost(row) {
  const value = finite(row?.cost_value);
  if (value === null) return '—';
  return `${value.toFixed(2)}${row?.currency ? ` ${row.currency}` : ''}`;
}

function countryChip(calc) {
  const date = calc.lastSuccess ? formatDate(eventIso(calc.lastSuccess)) : 'brak';
  return `<span class="diagnostics-country-chip ${calc.status.kind}"><b>${esc(calc.countryCode || '—')}</b> ${esc(date)}</span>`;
}

function historyKey(line, country, calculator) {
  return `${String(line || '').trim()}::${String(country || '').trim()}::${String(calculator || '').trim()}`;
}

function sourceLabel(source, calculatorKey) {
  if (String(source || '').toLowerCase().includes('oracle')) return 'Oracle';
  if (String(source || '').toLowerCase().includes('browserless')) return 'Browserless';
  if (String(source || '').toLowerCase().includes('chrome')) return 'Chrome';
  // Wszystkie bieżące kalkulatory poza e-TOLL działają przez Browserless.
  // Chrome pozostaje wyłącznie etykietą dla starych wpisów historycznych.
  return calculatorKey === 'etoll-pl' ? 'Oracle' : 'Browserless';
}

function settingsFromRaw(raw) {
  if (!raw || typeof raw !== 'object') return '';
  return raw.settingsSummary || raw.vehicleSettingsText || '';
}

function maxDate(values) {
  return (values || []).filter(Boolean).sort((a,b) => compareDates(b,a))[0] || '';
}

function worstStatus(kinds) {
  const order = ['error','missing','stale','ok','neutral'];
  return order.find(kind => kinds.includes(kind)) || 'neutral';
}

function countKinds(values) {
  return values.reduce((acc, value) => {
    acc[value] = (acc[value] || 0) + 1;
    return acc;
  }, {});
}

function compareDates(a,b) {
  return new Date(a || 0).getTime() - new Date(b || 0).getTime();
}

function groupBy(rows, keyFn) {
  const map = new Map();
  for (const row of rows || []) {
    const key = keyFn(row);
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(row);
  }
  return map;
}

function countryLabel(code) {
  return ({ PL:'Polska', CZ:'Czechy', SK:'Słowacja', AT:'Austria', HU:'Węgry', SI:'Słowenia', HR:'Chorwacja', IT:'Włochy', FR:'Francja' })[code] || code || 'Nieznane';
}

function formatDate(value) {
  if (!value) return 'brak';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'brak';
  return new Intl.DateTimeFormat('pl-PL', { day:'2-digit', month:'2-digit', year:'2-digit' }).format(date);
}

function formatDateTime(value) {
  if (!value) return 'brak';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'brak';
  return new Intl.DateTimeFormat('pl-PL', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }).format(date);
}

function finite(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function normalizeDirectionType(value) {
  const text = String(value || '').toLowerCase();
  if (/powr[oó]t|return|z powrotem/.test(text)) return 'return';
  if (/wyjazd|outbound|tam/.test(text)) return 'outbound';
  return text === 'return' || text === 'outbound' ? text : '';
}

function normalize(value) {
  return String(value || '').toLocaleLowerCase('pl-PL').normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

function esc(value) {
  return String(value ?? '').replace(/[&<>"']/g, char => ({ '&':'&amp;', '<':'&lt;', '>':'&gt;', '"':'&quot;', "'":'&#39;' })[char]);
}

function escAttr(value) { return esc(value); }

function setStatus(message, kind = 'neutral') {
  const el = $('diagnosticsStatus');
  if (!el) return;
  el.textContent = message;
  el.className = `status ${kind}`;
}

function setText(id, value) {
  const el = $(id);
  if (el) el.textContent = String(value ?? '0');
}

function clearObjectUrls() {
  for (const url of objectUrls) URL.revokeObjectURL(url);
  objectUrls = [];
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initDiagnostics, { once:true });
} else {
  initDiagnostics();
}
